/**
 * Story.js
 *
 * Phase C (June 2026) — AI-generated Story Campaign journal at /story.
 * Each player gets a unique 5-chapter campaign (GPT-generated). Chapter
 * objectives reuse server-verified gameplay events (kill/scan/explore/
 * earn/sell/survey) so progress accrues automatically while playing.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  BookOpen, Lock, CheckCircle2, Loader2, Sparkles, ArrowLeft, Crosshair,
  ScanSearch, Footprints, Coins, PackageOpen, Radar, ScrollText,
} from 'lucide-react';

const KIND_ICONS = {
  kill: Crosshair, scan: ScanSearch, explore: Footprints,
  earn: Coins, sell: PackageOpen, survey: Radar,
};

export default function Story() {
  const { api, updateCredits } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/api/story');
      setData(d);
    } catch (_e) {
      setData({ has_campaign: false });
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setBusy('start');
    try {
      const { data: r } = await api.post('/api/story/start');
      setData({ has_campaign: true, campaign: r.campaign });
      toast.success('Your campaign begins. Good hunting, pilot.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start the campaign'); }
    finally { setBusy(''); }
  };

  const claim = async () => {
    setBusy('claim');
    try {
      const { data: r } = await api.post('/api/story/claim');
      if (r.credits != null) updateCredits?.(r.credits);
      toast.success(`Chapter complete · +${r.reward.toLocaleString()} cr`);
      if (r.completed) toast.success('Campaign complete! Read your epilogue.', { duration: 6000 });
      setData({ has_campaign: true, campaign: r.campaign });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Chapter not complete yet'); }
    finally { setBusy(''); }
  };

  const c = data?.campaign;
  const completedCampaign = c?.completed;

  return (
    <div className="min-h-screen">
      <Header breadcrumbs={[{ label: 'Campaign' }]} />
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-amber-300" />
            <div>
              <h1 className="text-3xl font-bold text-primary" data-testid="story-title">Story Campaign</h1>
              <p className="text-sm text-muted-foreground">A narrative written for you alone, played out across the expanse.</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/explore/octants')} data-testid="story-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>

        {data === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-amber-400/60" /></div>
        ) : !data.has_campaign ? (
          <div className="text-center py-16 border border-amber-400/15 rounded-xl bg-white/[0.02]" data-testid="story-empty">
            <Sparkles className="w-12 h-12 mx-auto text-amber-400/50 mb-4" />
            <p className="text-lg text-slate-200">No campaign yet — your story is waiting to be written.</p>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              The narrative engine will generate a <span className="text-amber-300">unique 5-chapter arc</span> just
              for you. Objectives complete automatically as you play: fight, scan, explore, trade.
            </p>
            <Button
              className="mt-6 bg-amber-500/90 hover:bg-amber-400 text-slate-900 font-semibold"
              onClick={start} disabled={busy === 'start'}
              data-testid="story-start-btn"
            >
              {busy === 'start'
                ? (<span className="flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing your story…</span>)
                : (<span className="flex items-center"><Sparkles className="w-4 h-4 mr-2" /> Begin Campaign</span>)}
            </Button>
          </div>
        ) : (
          <div className="space-y-5" data-testid="story-detail">
            {/* Title + premise */}
            <div className="rounded-xl border border-amber-400/25 bg-gradient-to-br from-amber-950/25 to-transparent p-5">
              <div className="text-[11px] tracking-[0.3em] uppercase text-amber-400/70 flex items-center gap-2">
                <ScrollText className="w-3.5 h-3.5" /> {completedCampaign ? 'Campaign Complete' : 'Active Campaign'}
              </div>
              <h2 className="text-2xl font-bold text-amber-100 mt-1" data-testid="story-campaign-title">{c.title}</h2>
              <p className="text-sm text-slate-300 mt-2 leading-relaxed italic">{c.premise}</p>
            </div>

            {/* Chapters */}
            <div className="space-y-3">
              {(c.chapters || []).map((ch) => {
                const Icon = KIND_ICONS[ch.kind] || Crosshair;
                const isCurrent = !completedCampaign && ch.idx === c.current_chapter;
                const isDone = ch.claimed;
                const isLocked = !isDone && !isCurrent;
                const ready = isCurrent && (ch.progress || 0) >= ch.target;
                return (
                  <div
                    key={ch.idx}
                    className={[
                      'rounded-xl border p-4 transition-colors',
                      isDone ? 'border-emerald-400/25 bg-emerald-900/10'
                        : isCurrent ? 'border-amber-400/40 bg-amber-900/10 shadow-[0_0_24px_rgba(251,191,36,0.08)]'
                        : 'border-slate-700/40 bg-white/[0.015] opacity-60',
                    ].join(' ')}
                    data-testid={`story-chapter-${ch.idx}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {isDone ? <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                            : isLocked ? <Lock className="w-4 h-4 text-slate-500" />
                            : <Icon className="w-4 h-4 text-amber-300" />}
                          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400">Chapter {ch.idx + 1}</span>
                        </div>
                        <div className={`font-semibold mt-0.5 ${isDone ? 'text-emerald-100' : 'text-slate-100'}`}>{ch.title}</div>
                        {!isLocked && (
                          <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{ch.narrative}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs text-slate-400 uppercase tracking-[0.18em]">Reward</div>
                        <div className="text-amber-200 font-mono">+{ch.reward.toLocaleString()} cr</div>
                      </div>
                    </div>

                    {isCurrent && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-amber-200/90 flex items-center gap-1.5">
                            <Icon className="w-3.5 h-3.5" /> {ch.objective_label}
                          </span>
                          <span className="font-mono text-slate-300" data-testid="story-progress">
                            {Math.floor(ch.progress || 0)} / {Math.floor(ch.target)}
                          </span>
                        </div>
                        <Progress
                          value={Math.min(100, ((ch.progress || 0) / (ch.target || 1)) * 100)}
                          className="h-2 bg-slate-900/80"
                        />
                        {ch.location_hint && (
                          <p className="text-xs text-slate-500 mt-1.5">Hint: {ch.location_hint}</p>
                        )}
                        <div className="flex justify-end mt-2">
                          <Button
                            size="sm"
                            onClick={claim}
                            disabled={busy === 'claim' || !ready}
                            className="bg-amber-500/90 hover:bg-amber-400 text-slate-900 font-semibold disabled:bg-slate-700 disabled:text-slate-400"
                            data-testid="story-claim-btn"
                          >
                            {busy === 'claim' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            {ready ? 'Complete Chapter' : 'In Progress…'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Epilogue + restart */}
            {completedCampaign && (
              <div className="rounded-xl border border-emerald-400/25 bg-emerald-900/10 p-5" data-testid="story-epilogue">
                <div className="text-[11px] tracking-[0.3em] uppercase text-emerald-300/80 mb-2">Epilogue</div>
                <p className="text-sm text-slate-200 leading-relaxed italic">{c.epilogue}</p>
                <Button
                  className="mt-4 bg-amber-500/90 hover:bg-amber-400 text-slate-900 font-semibold"
                  onClick={start} disabled={busy === 'start'}
                  data-testid="story-restart-btn"
                >
                  {busy === 'start'
                    ? (<span className="flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing your next story…</span>)
                    : (<span className="flex items-center"><Sparkles className="w-4 h-4 mr-2" /> Begin a New Campaign</span>)}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
